import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { PaginatedListAspectRatioHelper } from '@theme/paginated-list-aspect-ratio';
import { StandardEvents } from '@shopify/events';

/** Product results shared by every category: reveal eight cards per explicit click. */
export default class PaginatedList extends Component {
  pages = new Map();
  infinityScrollObserver;
  #limit = 8;
  #busy = false;
  #generation = 0;
  #controller;
  #observer = new MutationObserver(() => this.#sync());
  #button;
  #status;
  #controls;
  #aspectRatioHelper;
  #error = false;

  connectedCallback() {
    super.connectedCallback();
    const templateCard = this.querySelector('[ref="cardGallery"]');
    if (templateCard) this.#aspectRatioHelper = new PaginatedListAspectRatioHelper({ templateCard });
    document.addEventListener(StandardEvents.searchUpdate, this.#reset);
    document.addEventListener(StandardEvents.collectionUpdate, this.#reset);
    this.#observer.observe(this, { childList: true, subtree: true });
    this.#sync();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#sync();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#observer.disconnect();
    this.#controller?.abort();
    this.#generation++;
    this.#busy = false;
    document.removeEventListener(StandardEvents.searchUpdate, this.#reset);
    document.removeEventListener(StandardEvents.collectionUpdate, this.#reset);
  }

  get sectionId() {
    const id = this.getAttribute('section-id');
    if (!id) throw new Error('The section-id attribute is required');
    return id;
  }

  #cards(grid) { return Array.from(grid.querySelectorAll(':scope > [data-product-id], :scope > [ref="cards[]"]')); }

  #sync() {
    const grid = this.querySelector('[ref="grid"]');
    if (!grid) return;
    if (!document.getElementById('vanity-load-more-style')) {
      const style = document.createElement('style');
      style.id = 'vanity-load-more-style';
      style.textContent = '.vanity-load-more-controls{grid-column:1/-1;width:100%;text-align:center;margin:28px auto 16px}.vanity-load-more{min-height:46px;padding:12px 26px;border:1px solid #bca66e;border-radius:6px;color:#25221d;background:linear-gradient(120deg,#fff 0%,#f6f4ef 33%,#fff 55%,#eee8da 80%,#fff 100%);font:inherit;font-weight:700;cursor:pointer}.vanity-load-more:disabled{opacity:.65;cursor:wait}.vanity-load-more-status{font-size:14px;margin:10px 0 0}.vanity-load-more-controls [hidden],results-list [data-vanity-card-hidden]{display:none!important}';
      document.head.append(style);
    }
    if (!this.#controls?.isConnected) {
      this.querySelectorAll('.vanity-load-more-controls').forEach(node => node.remove());
      this.#controls = document.createElement('div');
      this.#controls.className = 'vanity-load-more-controls';
      this.#button = document.createElement('button');
      this.#button.type = 'button';
      this.#button.className = 'vanity-load-more';
      this.#button.addEventListener('click', this.#more);
      this.#status = document.createElement('p');
      this.#status.className = 'vanity-load-more-status';
      this.#status.setAttribute('role', 'status');
      this.#controls.append(this.#button, this.#status);
      grid.after(this.#controls);
    }
    const cards = this.#cards(grid);
    cards.forEach((card, index) => {
      const hidden = index >= this.#limit;
      if (hidden) card.setAttribute('data-vanity-card-hidden', '');
      else card.removeAttribute('data-vanity-card-hidden');
      if (card.hidden !== hidden) card.hidden = hidden;
    });
    this.querySelectorAll('[ref="viewMoreNext"],[ref="viewMorePrevious"],.pagination').forEach(node => { node.hidden = true; node.style.display = 'none'; });
    const page = Number(cards.at(-1)?.dataset.page || new URL(location.href).searchParams.get('page') || 1);
    const hasMore = cards.length > this.#limit || page < Number(grid.dataset.lastPage || 1);
    this.#button.hidden = !hasMore;
    this.#button.disabled = this.#busy;
    const label = this.#busy ? 'Caricamento…' : this.#error ? 'Riprova' : 'Mostra altri prodotti';
    if (this.#button.textContent !== label) this.#button.textContent = label;
    this.#status.hidden = !this.#status.textContent;
  }

  #more = async () => {
    if (this.#busy) return;
    const grid = this.querySelector('[ref="grid"]');
    if (!grid) return;
    const generation = this.#generation;
    const target = this.#limit + 8;
    this.#busy = true;
    this.#error = false;
    this.#status.textContent = '';
    this.#controller = new AbortController();
    this.#sync();
    try {
      let cards = this.#cards(grid);
      while (cards.length < target) {
        const page = Number(cards.at(-1)?.dataset.page || 1) + 1;
        if (page > Number(grid.dataset.lastPage || 1)) break;
        const url = new URL(location.href);
        url.searchParams.set('page', String(page));
        url.hash = '';
        const html = await sectionRenderer.getSectionHTML(this.sectionId, false, url);
        if (generation !== this.#generation || this.#controller.signal.aborted || !this.isConnected || grid !== this.querySelector('[ref="grid"]')) return;
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const incoming = Array.from(doc.querySelectorAll('[ref="grid"] > [data-product-id], [ref="grid"] > [ref="cards[]"]'));
        const ids = new Set(cards.map(card => card.dataset.productId || card.id));
        const fresh = incoming.filter(card => !ids.has(card.dataset.productId || card.id));
        if (!fresh.length) throw new Error('Missing products');
        fresh.forEach(card => { card.hidden = true; card.setAttribute('data-vanity-card-hidden', ''); });
        grid.append(...fresh);
        this.#aspectRatioHelper?.processNewElements();
        cards = this.#cards(grid);
      }
      this.#limit = target;
      this.#status.textContent = 'Visualizzati ' + Math.min(this.#limit, this.#cards(grid).length) + ' prodotti.';
    } catch (error) {
      if (generation !== this.#generation || this.#controller?.signal.aborted) return;
      this.#error = true;
      this.#status.textContent = 'Non è stato possibile caricare altri prodotti. Riprova.';
    } finally {
      if (generation === this.#generation) { this.#busy = false; this.#sync(); }
    }
  };

  get vanityVisibleCount() { return this.#limit; }

  async restoreVanityVisibleCount(count, signal) {
    const target = Math.max(8, Math.ceil((Number(count) || 8) / 8) * 8);
    const started = performance.now();
    const abort = () => this.#controller?.abort();
    signal?.addEventListener('abort', abort, {once: true});
    try {
      while (this.#limit < target && !signal?.aborted && this.isConnected) {
        if (performance.now() - started > 30000) break;
        if (this.#busy) { await new Promise(resolve => setTimeout(resolve, 80)); continue; }
        await this.#more();
        if (this.#error) break;
      }
    } finally {
      signal?.removeEventListener('abort', abort);
    }
    return this.#limit;
  }

  #reset = (event) => {
    const section = event.target?.closest?.('[id^="shopify-section-"]');
    if (section && section.id !== 'shopify-section-' + this.sectionId) return;
    this.#controller?.abort();
    this.#generation++;
    this.#busy = false;
    this.#error = false;
    this.#limit = 8;
    this.pages.clear();
    if (this.#status) this.#status.textContent = '';
    this.#sync();
  };
}

